"use strict";

//=====================
// The client HTTP layer.
//
// It used to be an axios wrapper; it is now a thin fetch wrapper (report §3/R3,
// §10.14 P5.1): axios cost ~50 KiB in every page bundle for four call sites that
// only need JSON in and JSON out. The call SHAPES the pages use are preserved so
// the consumers (client/pages/*/data_access.js) stay unchanged:
//
//   http({url, method: "head"})            — the download pre-check
//   http.post(url, payload)                — JSON body, content-type: application/json
//   http.post(url, payload, {headers})     — e.g. the captcha `x-challenge`
//   http.get(url)
//   http.text(url, {noCommonHeaders: true}) — the RAW body, for a static asset
//                                             (`client/world_map.js`); the API
//                                             common headers are not sent
//
// and so is the rejection shape axios handed back, which the pages read:
//   error.response.data · error.response.headers["x-message"] · error.request · error.message.
//
// axios STAYS a package.json dependency: server/drivers/http.js still uses it for
// the upstream API calls. Only the CLIENT bundle drops it.
//=====================

const staticData = require("client/static_data");

const COMMON_HEADERS = {
    "x-lang": staticData.lang,
    "x-page": staticData.page,
    "x-requested-with": "XMLHttpRequest",
    "x-browser-width": String(window.innerWidth || Math.max(document.documentElement.clientWidth, document.body.clientWidth)),
    "x-browser-height": String(window.innerHeight || Math.max(document.documentElement.clientHeight, document.body.clientHeight))
};

function _isAbsolute (url) {
    return /^[a-z][a-z0-9+.-]*:\/\//i.test(url) || url.indexOf("//") === 0;
}

function _resolve (url) {
    if (_isAbsolute(url)) {
        return url;
    }
    return staticData.base_url + (url.charAt(0) === "/" ? url : "/" + url);
}

function _parse (text) {
    if (!text) {
        return null;
    }
    try {
        return JSON.parse(text);
    } catch (e) {
        return text;
    }
}

//=====================
// The rejection the pages expect. fetch's Headers is NOT bracket-addressable and
// bicsearch/bictoleisearch read error.response.headers["x-message"], so the
// headers are handed back as a plain object (lower-cased names, as fetch does).
//=====================
function _rejection (response, data) {
    const error = new Error("Request failed with status code " + response.status);
    error.response = {
        status: response.status,
        headers: Object.fromEntries(response.headers.entries()),
        data: data
    };
    error.request = response;
    return error;
}

async function _request (config) {
    const headers = {...(config.noCommonHeaders ? {} : COMMON_HEADERS), ...config.headers};
    const method = (config.method || "get").toUpperCase();
    const options = {
        method: method,
        headers: headers
    };

    if (config.data !== undefined && method !== "GET" && method !== "HEAD") {
        options.body = JSON.stringify(config.data);
        if (!headers["content-type"] && !headers["Content-Type"]) {
            headers["content-type"] = "application/json";
        }
    }

    const response = await fetch(_resolve(config.url), options);
    const body = method === "HEAD" ? "" : await response.text();
    let data = null;
    if (method !== "HEAD") {
        data = config.responseType === "text" ? body : _parse(body);
    }

    if (!response.ok) {
        throw _rejection(response, data);
    }

    return {
        status: response.status,
        data: data,
        headers: Object.fromEntries(response.headers.entries())
    };
}

function http (config) {
    return _request(config);
}

http.get = function (url, config) {
    return _request({...config, url: url, method: "get"});
};

http.post = function (url, data, config) {
    return _request({...config, url: url, method: "post", data: data});
};

http.head = function (url, config) {
    return _request({...config, url: url, method: "head"});
};

http.text = function (url, config) {
    return _request({...config, url: url, method: "get", responseType: "text"});
};

module.exports = http;
